// SelectInput.jsx

import React, { useEffect, useMemo, useRef, useState, forwardRef } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './SelectInput.css';

// Components
import PopupOption from 'components/popup/option/PopupOption';
import Popup from 'components/popup/Popup';
import TextInput from 'components/textinput/TextInput';

/**
 * SelectInput
 *
 * Custom select input that renders options in a popup and supports rich option content.
 *
 * Option shape:
 * - value: string | number
 * - label: string
 * - iconKey?: string
 * - note?: string
 * - color?: string
 * - disabled?: boolean
 *
 * @param {boolean} [showIcons=true] When false, option icons are omitted from
 *   the trigger and list (startIconKey still applies if provided).
 * @param {boolean} [showFilter=false] When true, shows a filter field at the
 *   top of the options popup.
 * @param {string} [filterPlaceholder='Filter...'] Placeholder for the filter field.
 * @param {string} [footerActionLabel] Optional sticky footer action label.
 * @param {function} [onFooterAction] Called with the current filter query when
 *   the footer action is pressed. Closes the popup first.
 *
 * @returns {ReactNode} The rendered select control.
 */
const SelectInput = forwardRef(({
  value,
  onChange,
  onValueChange,
  options = [],
  placeholder = 'Select...',
  helperText = '',
  error = false,
  disabled = false,
  style = {},
  className = '',
  hybrid = false,
  iconSize = 22,
  startIconKey,
  endIconKey = 'keyboard_arrow_down',
  showIcons = true,
  showFilter = false,
  filterPlaceholder = 'Filter...',
  footerActionLabel = '',
  onFooterAction,
  popupZIndex = 2000,
  emptyText = 'No options available',
  width = '100%',
  ...rest
}, ref) => {

  // Theme Context
  const { theme } = useTheme();

  const isMobile = useIsMobile();

  const [isOpen, setOpen] = useState(false);
  const [popupWidth, setPopupWidth] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');

  const anchorRef = useRef(null);

  const normalizedOptions = useMemo(() => (
    (Array.isArray(options) ? options : []).map((option, index) => {
      const derivedValue = option?.value ?? option?.key ?? option?.id ?? '';
      const normalizedValue = String(derivedValue);
      const normalizedLabel = String(option?.label ?? '');
      const normalizedColor = typeof option?.color === 'string'
        ? option.color.trim()
        : '';

      return {
        ...option,
        value: normalizedValue,
        label: normalizedLabel,
        iconKey: option?.iconKey || '',
        iconResource: option?.iconResource || '',
        note: option?.note || '',
        color: normalizedColor,
        disabled: Boolean(option?.disabled),
        _listKey: normalizedValue || `${normalizedLabel || '__empty__'}_${index}`,
      };
    })
  ), [options]);

  const filteredOptions = useMemo(() => {
    if (!showFilter) return normalizedOptions;
    const query = String(filterQuery || '').trim().toLowerCase();
    if (!query) return normalizedOptions;
    return normalizedOptions.filter((option) => {
      const label = String(option.label || '').toLowerCase();
      const optionValue = String(option.value || '').toLowerCase();
      const note = String(option.note || '').toLowerCase();
      return label.includes(query) || optionValue.includes(query) || note.includes(query);
    });
  }, [filterQuery, normalizedOptions, showFilter]);

  const selectedValue = useMemo(() => (
    value == null || value === '' ? '' : String(value)
  ), [value]);

  const selectedOption = useMemo(
    () => normalizedOptions.find((option) => option.value === selectedValue) || null,
    [normalizedOptions, selectedValue]
  );

  useEffect(() => {
    if (!isOpen || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    if (rect?.width) setPopupWidth(rect.width);
  }, [isOpen, normalizedOptions.length, selectedValue]);

  useEffect(() => {
    if (!isOpen) setFilterQuery('');
  }, [isOpen]);

  const emitChange = (nextValue, option) => {
    const eventLike = {
      target: {
        value: nextValue,
        name: rest.name,
      },
      currentTarget: {
        value: nextValue,
        name: rest.name,
      },
    };

    onChange && onChange(eventLike);
    onValueChange && onValueChange(nextValue, option);
  };

  /**
   * handleSelect
   *
   * Handles a selection change.
   *
   * @param {unknown} option - option.
   */
  const handleSelect = (option) => {
    if (!option || option.disabled) return;
    emitChange(option.value, option);
    setOpen(false);
  };

  /**
   * handleKeyDown
   *
   * Handles a key down event.
   *
   * @param {object} event - event.
   */
  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
    }

    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const displayValue = selectedOption?.label || '';
  const triggerStartIconKey = showIcons
    ? (selectedOption?.iconKey || startIconKey)
    : startIconKey;
  const hasFilterQuery = Boolean(String(filterQuery || '').trim());
  const emptyMessage = normalizedOptions.length === 0
    ? emptyText
    : (hasFilterQuery ? 'No matches' : emptyText);
  const showFooterAction = typeof onFooterAction === 'function'
    && Boolean(String(footerActionLabel || '').trim());

  const handleFooterAction = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!showFooterAction || disabled) return;
    const query = String(filterQuery || '');
    setOpen(false);
    onFooterAction(query);
  };

  return (
    <div
      className={`select-input-container ${className}`.trim()}
      style={{
        width: width,
      }}
    >

      {/* TRIGGER */}
      <div
        className="select-input-trigger"
        style={{
        width: width,
      }}
        ref={anchorRef}
      >
        <TextInput
          style={{ width: '100%', cursor: disabled ? 'not-allowed' : 'pointer', ...style }}
          ref={ref}
          value={displayValue}
          readOnly
          disabled={disabled}
          placeholder={placeholder}
          startIconKey={triggerStartIconKey}
          startIconColor={theme.foregroundMuted}
          endIconKey={endIconKey}
          endIconColor={theme.foregroundMuted}
          iconSize={iconSize}
          helperText={helperText}
          error={error}
          hybrid={hybrid}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={rest.id ? `${rest.id}-listbox` : undefined}
          {...rest}
          width={width}
          onClick={() => !disabled && setOpen((prev) => !prev)}
          onKeyDown={handleKeyDown}
        />
      </div>

      <Popup anchorRef={anchorRef} isOpen={isOpen} setOpen={setOpen} zIndex={popupZIndex}>

        {/* POPUP */}
        <div
          className="select-input-popup"
          style={{
            maxHeight: isMobile ? "60vh" : "300px",
            minWidth: isMobile ? "100%" : popupWidth ? `${popupWidth}px` : undefined,
          }}
          id={rest.id ? `${rest.id}-listbox` : undefined}
          role="listbox"
        >

          {/* FILTER */}
          {showFilter ? (
            <div className="select-input-filter">
              <TextInput
                value={filterQuery}
                placeholder={filterPlaceholder}
                style={{ 
                  background: theme.popup,
                  border: `1px solid ${theme.outlineMuted}`,
                  borderRadius: '8px',
                }}
                width="100%"
                iconSize={18}
                startIconKey="search"
                startIconColor={theme.foregroundMuted}
                onChange={(event) => setFilterQuery(event?.target?.value ?? '')}
              />
            </div>
          ) : null}

          {/* OPTIONS */}
          <div className="select-input-list">
            {filteredOptions.length === 0 ? (
              <div className="select-input-empty">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = option.value === selectedValue;
                return (
                  <PopupOption
                    key={`${option._listKey}_${index}`}
                    iconKey={showIcons ? (option.iconKey || undefined) : undefined}
                    iconResource={showIcons ? (option.iconResource || undefined) : undefined}
                    label={option.label}
                    note={option.note || undefined}
                    selected={isSelected}
                    disabled={option.disabled}
                    onClick={() => handleSelect(option)}
                    rightContent={option.color ? (
                      <span
                        className="select-input-color-dot"
                        style={{ backgroundColor: option.color }}
                      />
                    ) : undefined}
                  />
                );
              })
            )}
          </div>

          {/* FOOTER ACTION */}
          {showFooterAction ? (
            <div
              className="select-input-footer-action"
              onClick={handleFooterAction}
              onMouseDown={(event) => event.preventDefault()}
            >
              {footerActionLabel}
            </div>
          ) : null}
        </div>
      </Popup>
    </div>
  );
});

// Dev Tools
SelectInput.displayName = 'SelectInput';

export default SelectInput;
